import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  SimpleSearchBar,
  Spinner,
  Text,
  cn,
  useToast,
} from '@flatfile/baseline'
import { Suspense, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { UserWithRoles, resendUserInvite } from '../../api/internal/users'
import { ResourceList } from '../../components/ResourceList/ResourceList'
import { ResourceListContextProvider } from '../../components/ResourceList/ResourceListContext'
import { ListPagination, ResourceRowComponent } from '../../components/ResourceList/types'
import { useDebounce } from '../../hooks/useDebounce'
import { useDynamicHeight } from '../../hooks/useDynamicHeight'
import { useIdentity } from '../../hooks/useIdentity'
import { useCollection } from '../../resources/hooks/useCollection'
import useResource from '../../resources/hooks/useResource'
import { useDeleteResource } from '../../resources/hooks/useResourceMutation'
import { formatDate, formatTime } from '../../utils/format'
import { logger } from '../../utils/logging'
import { ErrorDetail, UserModal } from './components/UserModal'

const UsersHeader = () => (
  <thead className='border-b border-gray-200'>
    <tr>
      <th className='px-3 py-2 text-left align-middle h-[42px]'>
        <Text variant='subheadline' emphasized className='text-muted'>
          Name
        </Text>
      </th>
      <th className='px-3 py-2 text-left align-middle h-[42px]'>
        <Text variant='subheadline' emphasized className='text-muted'>
          Email Address
        </Text>
      </th>
      <th className='px-3 py-2 text-left align-middle h-[42px]'>
        <Text variant='subheadline' emphasized className='text-muted'>
          Roles
        </Text>
      </th>
      <th className='px-3 py-2 text-left align-middle h-[42px]'>
        <Text variant='subheadline' emphasized className='text-muted'>
          Date joined
        </Text>
      </th>
      <th className='px-3 py-2 text-left align-middle h-[42px]'>
        <Text variant='subheadline' emphasized className='text-muted'>
          Last seen
        </Text>
      </th>
      <th className='px-3 py-2 text-left align-middle h-[42px]'></th>
    </tr>
  </thead>
)

const DeleteUserModal = ({ user, onClose }: { user: UserWithRoles; onClose: () => void }) => {
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<ErrorDetail[]>([])
  const [deleteUser] = useDeleteResource('USERS')
  const handleClick = async () => {
    setLoading(true)
    try {
      await deleteUser(user.id)
      setLoading(false)
      onClose()
    } catch (error: any) {
      setLoading(false)
      setErrors(error?.data?.errors ?? [])
      logger.error('Failed to delete user', error ?? {})
    }
  }

  return (
    <div className='flex flex-col gap-4'>
      <Text>
        You are about to permanently delete this user. This action cannot be reversed.
        <Text as='span' emphasized>
          {' '}
          Are you sure you want to proceed in deleting {user.name}?
        </Text>
      </Text>
      <Button variant='destructive' onClick={handleClick} disabled={loading}>
        {loading ? 'Deleting...' : 'I understand, delete this user'}
      </Button>
      {errors.length > 0 && <Alert variant='destructive'>{errors.map((err) => err.message).join(', ')}</Alert>}
    </div>
  )
}

const getResourceIcon = (resourceId?: string) => {
  if (!resourceId) {
    return <Icon name='triangle-exclamation' size={12} className='text-red-500' />
  }
  const identifier = resourceId.split('_')[1]
  switch (identifier) {
    case 'env':
      return <Icon name='growth' size={12} />
    case 'sp':
      return <Icon name='earth' size={12} />
    default:
      return <Icon name='people' size={12} />
  }
}

const renderUserRoles = (user: UserWithRoles) => {
  const hasNoRoles = !user.roles || user.roles.length === 0
  const firstRole = user.roles?.[0]
  const noAdditionalRoles = user.roles?.length > 0 ? user.roles.length - 1 : 0

  if (hasNoRoles) {
    return (
      <div className='flex items-center gap-1.5 mt-0.75'>
        {getResourceIcon(firstRole?.resourceId)}
        <Text className='text-xs font-bold'>No roles assigned</Text>
      </div>
    )
  }
  return (
    <div className='flex gap-2'>
      <div className='border border-gray-300 rounded-sm px-2 py-0.5'>
        <div className='flex items-center gap-1.5'>
          {getResourceIcon(firstRole?.resourceId)}
          <Text className='text-xs font-medium'>{firstRole?.resourceName}</Text>
          <Text className='text-xs font-medium text-gray-500 mt-0.25 text-[9px]'>
            / {firstRole?.name.toUpperCase()}
          </Text>
        </div>
      </div>
      {noAdditionalRoles > 0 && (
        <div className='border border-gray-300 rounded-sm px-2 py-0.5'>
          <Text className='text-xs font-medium'>+{noAdditionalRoles}</Text>
        </div>
      )}
    </div>
  )
}

const renderCreatedAt = (user: UserWithRoles) => {
  return user.createdAt && !user.hasPendingInvite ? (
    <>
      {formatDate(user.createdAt)} {formatTime(user.createdAt)}
    </>
  ) : (
    <div className='w-fit border border-gray-300 rounded-sm px-2 py-0.5'>
      <Text className='text-xs font-medium'>Pending approval</Text>
    </div>
  )
}

export const ManageUsersPage = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageCount, setPageCount] = useState(0)
  const [resetCache, setResetCache] = useState(0)
  const [requestParams, setRequestParams] = useState<{ search?: string; sortDirection: string }>({
    sortDirection: 'desc',
  })
  const pagination: ListPagination = { currentPage, setCurrentPage, pageCount, setPageCount }
  const [searchValue, setSearchValue] = useState<string | undefined>()
  const debouncedSearchValue = useDebounce<string | undefined>(searchValue, 500)
  const users = useCollection('USERS', { pageNumber: currentPage, pageSize: 15 })

  // State for editing user
  const [editingUser, setEditingUser] = useState<UserWithRoles | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const inputRef = useRef<HTMLInputElement | null>(null)

  const onlyAccountAdminId = useMemo(() => {
    const adminUsers = users.filter((user) => {
      return user.roles.some((role) => {
        return role.name === 'Admin' && role.resourceName === 'Account'
      })
    })
    return adminUsers.length === 1 ? adminUsers[0].id : undefined
  }, [users, resetCache])

  const { addToRefsArray, listHeight } = useDynamicHeight()

  useEffect(() => {
    if (debouncedSearchValue) {
      inputRef?.current?.focus()
      setCurrentPage(1)
    }

    setRequestParams((currParams) => {
      const newParams = { ...currParams }
      if (!debouncedSearchValue && newParams.search) newParams.search = undefined
      else if (debouncedSearchValue) newParams.search = debouncedSearchValue
      return newParams
    })
  }, [debouncedSearchValue])

  const onSearch = useCallback((value: string) => {
    if (value) {
      setSearchValue(value)
    } else {
      setSearchValue(undefined)
    }
  }, [])

  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserWithRoles | null>(null)

  const handleInviteUser = () => {
    setIsUserModalOpen(true)
  }

  const handleCloseUserModal = () => {
    setIsUserModalOpen(false)
    setResetCache((prev) => prev + 1)
  }

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false)
    setSelectedUser(null)
    setResetCache((prev) => prev + 1)
  }

  const handleDeleteSuccess = () => {
    setCurrentPage(1)
    setResetCache((prev) => prev + 1)
  }

  const { toast } = useToast()

  const UsersRow: ResourceRowComponent = memo(({ resourceId }) => {
    const user = useResource('USERS', resourceId)
    const { identity } = useIdentity()

    if (!user) {
      return <></>
    }

    const handleEdit = () => {
      setSelectedUser(user)
      setEditingUser(user)
      setIsEditModalOpen(true)
    }

    const handleDelete = () => {
      setSelectedUser(user)
      setIsDeleteModalOpen(true)
    }

    const handleResendUserInvite = async () => {
      try {
        await resendUserInvite(user.id)
        toast({
          title: 'Success',
          description: 'User invite resent successfully',
        })
      } catch (error: any) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to resend user invite',
        })
        logger.error('Failed to resend user invite', error ?? {})
      }
    }

    return (
      <tr className='hover:bg-gray-50 rounded-md overflow-hidden p-1'>
        <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
          <Text className='text-sm font-normal'>{user.name}</Text>
        </td>
        <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
          <Text className='text-sm font-normal'>{user.email}</Text>
        </td>
        <td className='p-1 pl-2 text-left align-middle border-b border-gray-200 text-sm'>{renderUserRoles(user)}</td>
        <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
          <Text className='text-sm font-normal'>{renderCreatedAt(user)}</Text>
        </td>
        <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
          {user.lastSeenAt ? (
            <Text className='text-sm font-normal'>
              {formatDate(user.lastSeenAt)} {formatTime(user.lastSeenAt)}
            </Text>
          ) : (
            <Text className='text-sm text-muted'>-</Text>
          )}
        </td>
        <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                size='icon-md'
                icon='dot-grid-1x3-vertical'
                iconProps={{ size: 12 }}
                aria-label='More Options'
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onClick={handleEdit}>Edit user</DropdownMenuItem>
              <DropdownMenuItem onClick={handleResendUserInvite} disabled={identity.user.id === user.id}>
                Resend invite
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDelete} disabled={identity.user.id === user.id} className='text-red-500'>
                Delete user
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
      </tr>
    )
  })

  UsersRow.displayName = 'UsersRow'

  return (
    <>
      <ResourceListContextProvider requestParams={requestParams} resourceType='USERS'>
        <div ref={addToRefsArray} className='w-full h-full flex flex-col p-3'>
          <div className='p-3 flex justify-between items-center'>
            <Text variant='title2'>Manage Users</Text>
            <Button size='sm' onClick={handleInviteUser} icon='plus-large' iconPlacement='left'>
              Invite User
            </Button>
          </div>
          <div>
            <div ref={addToRefsArray}>
              <div className='flex justify-between items-center py-4 px-3'>
                <div className='relative pr-2'>
                  <SimpleSearchBar
                    ref={inputRef}
                    searchTerm={searchValue || ''}
                    onSearchChange={onSearch}
                    placeholder='Search users'
                    className='min-w-[250px]'
                    size='sm'
                  />
                </div>
                <Pagination className='py-2 justify-end' iconOnly size='sm'>
                  <PaginationContent>
                    <PaginationPrevious
                      onClick={() => {
                        if (currentPage > 1) setCurrentPage(currentPage - 1)
                      }}
                      disabled={currentPage <= 1}
                    />
                    {(() => {
                      const maxVisiblePages = 5
                      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
                      // Always show at least one page
                      const effectivePageCount = Math.max(1, pagination.pageCount)
                      const endPage = Math.min(effectivePageCount, startPage + maxVisiblePages - 1)

                      // Adjust start page if we're near the end
                      if (endPage - startPage + 1 < maxVisiblePages) {
                        startPage = Math.max(1, endPage - maxVisiblePages + 1)
                      }

                      // Generate array of page numbers to display
                      const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)

                      return pages.map((pageNum) => (
                        <PaginationItem key={pageNum}>
                          <PaginationLink
                            isActive={currentPage === pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={cn(
                              'w-5 h-5 flex items-center justify-center rounded-full p-0',
                              currentPage === pageNum
                                ? 'bg-surface-sentiment-subtle-neutral border-none'
                                : 'hover:bg-gray-100'
                            )}
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      ))
                    })()}
                    <PaginationNext
                      onClick={() => {
                        if (currentPage < pagination.pageCount) setCurrentPage(currentPage + 1)
                      }}
                      disabled={currentPage >= pagination.pageCount || pagination.pageCount <= 1}
                    />
                  </PaginationContent>
                </Pagination>
              </div>
            </div>
          </div>
          <div className='overflow-x-auto'>
            <div className='min-w-[780px]'>
              <ResourceList
                height={listHeight}
                pagination={pagination}
                headerComponent={UsersHeader}
                rowComponent={UsersRow}
                errorMessage='Error loading users.'
                emptyMessage='No users found.'
                forceMount={resetCache}
                onRowActionSuccess={handleDeleteSuccess}
              />
            </div>
          </div>
        </div>
      </ResourceListContextProvider>

      {/* Edit User Modal */}
      <Dialog
        open={isEditModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsEditModalOpen(false)
            setEditingUser(null)
            setSelectedUser(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <Suspense
            fallback={
              <div className='relative h-48'>
                <div className='absolute inset-0 flex items-center justify-center'>
                  <Spinner />
                </div>
              </div>
            }
          >
            {editingUser && (
              <UserModal
                mode='edit'
                user={editingUser}
                isOnlyAccountAdmin={onlyAccountAdminId === editingUser.id}
                onClose={() => {
                  setIsEditModalOpen(false)
                  setEditingUser(null)
                  setSelectedUser(null)
                }}
              />
            )}
          </Suspense>
        </DialogContent>
      </Dialog>

      <Dialog open={isUserModalOpen} onOpenChange={(open) => !open && handleCloseUserModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
          </DialogHeader>
          <Suspense
            fallback={
              <div className='relative h-48'>
                <div className='absolute inset-0 flex items-center justify-center'>
                  <Spinner />
                </div>
              </div>
            }
          >
            <UserModal mode='invite' onClose={handleCloseUserModal} isOnlyAccountAdmin={false} />
          </Suspense>
        </DialogContent>
      </Dialog>

      {selectedUser && (
        <Dialog open={isDeleteModalOpen} onOpenChange={(open) => !open && handleCloseDeleteModal()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete user</DialogTitle>
            </DialogHeader>
            <DeleteUserModal user={selectedUser} onClose={handleCloseDeleteModal} />
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
